import styles from './track-follow-bell.module.css'

import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiSettings } from '@react-icons/all-files/fi/FiSettings'
import { LuBell } from '@react-icons/all-files/lu/LuBell'
import { LuBellOff } from '@react-icons/all-files/lu/LuBellOff'
import { LuBellRing } from '@react-icons/all-files/lu/LuBellRing'
import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useFollowTrackMutation,
  useGetTrackFollowQuery,
} from '@disco/state-management/api/discussions'
import { useGetNotesNotificationPreferenceQuery } from '@disco/state-management/api/notification-preferences'
import { pushUINotification } from '@disco/utils/notifications'

import type { TrackFollowLevel } from 'types/discussion'
import type { TrackId } from 'types/track'

import { sendNotificationSettingEvent } from 'utils/discussion-mixpanel'

import {
  Box,
  Button,
  DropdownMenu,
  Flex,
  Text,
  VisuallyHidden,
} from 'components/ds'

type IconComponent = React.ComponentType<{ size?: number }>

const LEVEL_OPTIONS: {
  value: TrackFollowLevel
  label: string
  Icon: IconComponent
}[] = [
  { value: 'all', label: 'All notes', Icon: LuBellRing },
  {
    value: 'mentions_and_replies',
    label: 'Mentions and replies only',
    Icon: LuBellRing,
  },
  { value: 'off', label: 'Off', Icon: LuBellOff },
]

type Props = {
  trackId: TrackId
}

const TrackFollowBell = ({ trackId }: Props): React.ReactElement => {
  // The optimistic update lives in followTrack.onQueryStarted, so the bell just
  // renders the cache.
  const { data } = useGetTrackFollowQuery(trackId, {
    refetchOnMountOrArgChange: true,
  })
  const { data: globalPreference } = useGetNotesNotificationPreferenceQuery()
  const [followTrack] = useFollowTrackMutation()
  const navigate = useNavigate()

  // level null inherits the global default (never a hardcoded 'all'); undefined
  // until the preference query resolves.
  const effectiveLevel = data?.level ?? globalPreference?.activity_level
  // source null = no subscription, so the bell reflects the inherited default
  // rather than reading as a real follow.
  const isFollowing = data?.source != null

  const handleSelect = async (next: TrackFollowLevel): Promise<void> => {
    try {
      await followTrack({
        target_type: 'track',
        target_object_id: trackId,
        level: next,
      }).unwrap()
      // Picking any level turns an inherited or auto follow into a manual one
      if (data?.level !== next || data?.source !== 'manual') {
        sendNotificationSettingEvent(
          'notes_tab',
          {
            setting: 'activity_level',
            previous_value: effectiveLevel ?? null,
            new_value: next,
          },
          { target_type: 'track', target_object_id: trackId },
        )
      }
    } catch {
      pushUINotification({
        message:
          'Could not update your notification setting. Please try again.',
        level: 'error',
      })
    }
  }
  const currentOption = LEVEL_OPTIONS.find(o => o.value === effectiveLevel)

  // Plain bell while the level is unknown so the trigger never asserts a
  // state it doesn't have yet.
  const TriggerIcon = currentOption?.Icon ?? LuBell
  // Appended after the visible label so the accessible name starts with the
  // text a voice-control user can see (WCAG 2.5.3).
  const hiddenContext = !currentOption
    ? 'Track notifications'
    : isFollowing
    ? 'notifications, set for this track'
    : 'notifications, using your default'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        {/* Radix ghost buttons pull themselves out by their padding via
        negative margins; matching margins keep the box flush with the
        segmented control above */}
        <Button size="1" variant="ghost" color="gray" mx="2" my="1">
          <TriggerIcon size={16} aria-hidden />
          {currentOption?.label}
          <VisuallyHidden>{hiddenContext}</VisuallyHidden>
          <LuChevronDown size={16} aria-hidden />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        {LEVEL_OPTIONS.map(({ value, label, Icon }) => (
          <DropdownMenu.Item key={value} onSelect={() => handleSelect(value)}>
            <Flex align="center" gap="2" width="100%">
              <span className={styles.menuIcon}>
                <Icon size={16} />
              </span>
              <Text size="2">{label}</Text>
              {effectiveLevel === value && (
                <Box ml="auto" data-testid={`follow-level-current-${value}`}>
                  <FiCheck size={16} />
                </Box>
              )}
            </Flex>
          </DropdownMenu.Item>
        ))}
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={() => navigate('/settings/notifications')}>
          <Flex align="center" gap="2">
            <span className={styles.menuIcon}>
              <FiSettings size={16} />
            </span>
            <Text size="2">Manage all notifications</Text>
          </Flex>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default TrackFollowBell
